import type { ServerView } from '@hearth/shared';
import { linkTo } from '../router';
import { SleepButton, WakeButton } from '../ui/ActionButtons';
import { Campfire } from '../ui/Campfire';
import { CopyAddress } from '../ui/CopyAddress';
import { campfireFor, cardKind, joinAddress, may, metaText, slotText, statusText, statusTone } from './serverWords';

/**
 * One server: its campfire, name and status, and one bottom row of a fixed height, so the card
 * never changes size as the server wakes and sleeps. Only the top part (fire, name, arrow) opens
 * the server; the status line and the row never do, so near-misses on a phone open nothing.
 */
export function ServerCard({ server, now, onWake, onSleep }: { server: ServerView; now: Date; onWake: () => void; onSleep: () => void }) {
  const kind = cardKind(server);
  const address = joinAddress(server);
  return (
    <article className="server-card">
      <div className="server-head">
        <span className="server-fire"><Campfire state={campfireFor(server)} height={40} /></span>
        <div className="server-title">
          <h2><a className="server-link" {...linkTo(`/app/servers/${server.handle}`)}>{server.name}</a></h2>
          <p className="quiet">{metaText(server)}</p>
        </div>
        <svg className="server-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M7 17 17 7M9 7h8v8" />
        </svg>
      </div>
      <p className={`server-status server-status-${statusTone(server)}`} aria-live="polite">{statusText(server, now)}</p>
      <div className="server-row">
        {/* Its address (D4: its own name) awake or asleep, so friends can save it before it wakes. */}
        {(kind === 'running' || kind === 'asleep') && address ? (
          <>
            <CopyAddress value={address} />
            {kind === 'running'
              ? may(server, 'stop') && <SleepButton serverName={server.name} onClick={onSleep} />
              : may(server, 'start') && <WakeButton serverName={server.name} onClick={onWake} />}
          </>
        ) : (
          <>
            <span className={kind === 'changing' ? 'slot slot-busy' : 'slot'}>{slotText(server)}</span>
            {kind === 'asleep' && server.status !== 'DESTROYED' && may(server, 'start') && <WakeButton serverName={server.name} onClick={onWake} />}
          </>
        )}
      </div>
    </article>
  );
}
